// ============================================================================
// New Accounts — all-staff dashboard widget (Option D PR-5). The literal answer
// to Bryan's standing question: "what are our new accounts, when are they
// starting, which AM is responsible, and what do they need?" Read-only +
// RLS-scoped via v_new_account_lifecycle.
//
// Layout top→bottom: ⚠ Behind (RED, each row opens the D4 resolution dialog),
// Recent starts (≤14d, AMBER), Starting Soon (countdowns, soonest first).
// ============================================================================

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowUpRight, CalendarCheck, CalendarClock, MapPin } from 'lucide-react';
import { WidgetCard, WidgetSkeleton } from '../widget-card';
import { EmptyState } from '../../../shared/components';
import { supabase } from '../../../shared/supabase';
import { useNewAccounts, type NewAccountRow } from './use-new-accounts';
import { ResolutionDialog, type ResolutionTarget } from '../../new-account-sop/resolution-dialog';

interface Props {
  enabled: boolean;
}

function countdown(r: NewAccountRow): string {
  const n = r.daysUntilStart;
  if (n === null) return 'No start date';
  if (n < 0) return `${Math.abs(n)} day${Math.abs(n) === 1 ? '' : 's'} overdue`;
  if (n === 0) return 'Starts today';
  return `Starts in ${n} day${n === 1 ? '' : 's'}`;
}

function metaLine(r: NewAccountRow): string {
  return [r.marketRegion, r.amName ? `AM: ${r.amName}` : null, r.stage?.replace(/_/g, ' ')]
    .filter(Boolean)
    .join(' · ');
}

export function NewAccountsWidget({ enabled }: Props) {
  const { data, isLoading, error, refetch } = useNewAccounts(enabled);
  const [resolution, setResolution] = useState<ResolutionTarget | null>(null);
  if (!enabled) return null;

  const total = data ? data.behind.length + data.recent.length + data.soon.length : 0;

  async function cancelCard(sopId: string, reason: string) {
    const { error: e } = await supabase
      .from('new_account_sop')
      .update({
        status: 'Cancelled',
        stage: 'Cancelled',
        cancelled_date: new Date().toISOString().slice(0, 10),
        cancelled_reason: reason,
      })
      .eq('sop_id', sopId);
    if (e) throw e;
  }

  return (
    <WidgetCard
      title="New Accounts"
      subtitle="When they start, who owns them, and what's needed."
      minHeight={260}
    >
      {error && <p className="text-xs text-st-danger-fg">Couldn't load: {error}</p>}
      {!error && isLoading && <WidgetSkeleton lines={4} />}
      {!error && !isLoading && data && total === 0 && (
        <EmptyState title="No new accounts in flight" body="Starting-soon, behind, and recently started accounts will appear here." />
      )}
      {!error && !isLoading && data && total > 0 && (
        <div className="space-y-4">
          {data.behind.length > 0 && (
            <Section
              tone="red"
              icon={<AlertTriangle className="h-3.5 w-3.5" />}
              title={`Behind · ${data.behind.length}`}
              hint="start date passed, not confirmed"
            >
              {data.behind.map((r) => (
                <button
                  key={r.sopId}
                  type="button"
                  onClick={() =>
                    setResolution({
                      sopId: r.sopId,
                      facilityId: r.facilityId,
                      displayName: r.displayName,
                      plannedStartDate: r.plannedStartDate,
                    })
                  }
                  className="group flex w-full items-center justify-between gap-3 rounded-md px-2 py-2 text-left hover:bg-rag-red/5"
                >
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium text-tcs-text">{r.displayName}</span>
                    <span className="block truncate text-[11px] text-tcs-text-secondary">{metaLine(r)}</span>
                  </span>
                  <span className="shrink-0 text-right text-[11px] font-semibold text-rag-red">
                    {countdown(r)}
                    <span className="block text-[10px] font-normal text-tcs-text-muted">resolve →</span>
                  </span>
                </button>
              ))}
            </Section>
          )}

          {data.recent.length > 0 && (
            <Section
              tone="amber"
              icon={<CalendarCheck className="h-3.5 w-3.5" />}
              title={`Recent starts · ${data.recent.length}`}
              hint="live ≤ 14 days — watch closely"
            >
              {data.recent.map((r) => (
                <RowLink key={r.sopId} r={r} right={r.actualStartDate ? `Started ${r.actualStartDate}` : 'Started'} />
              ))}
            </Section>
          )}

          {data.soon.length > 0 && (
            <Section
              tone="plain"
              icon={<CalendarClock className="h-3.5 w-3.5" />}
              title={`Starting soon · ${data.soon.length}`}
              hint="next 14 days"
            >
              {data.soon.map((r) => (
                <RowLink key={r.sopId} r={r} right={countdown(r)} />
              ))}
            </Section>
          )}
        </div>
      )}

      {resolution && (
        <ResolutionDialog
          target={resolution}
          onClose={() => setResolution(null)}
          onResolved={refetch}
          onCancelCard={cancelCard}
        />
      )}
    </WidgetCard>
  );
}

function Section({
  tone,
  icon,
  title,
  hint,
  children,
}: {
  tone: 'red' | 'amber' | 'plain';
  icon: React.ReactNode;
  title: string;
  hint: string;
  children: React.ReactNode;
}) {
  const toneClass =
    tone === 'red' ? 'text-rag-red' : tone === 'amber' ? 'text-st-warn-fg' : 'text-tcs-text-secondary';
  return (
    <div>
      <div className={`mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide ${toneClass}`}>
        {icon}
        <span>{title}</span>
        <span className="font-normal normal-case tracking-normal text-tcs-text-muted">· {hint}</span>
      </div>
      <div className="-mx-2 divide-y divide-tcs-border/60">{children}</div>
    </div>
  );
}

function RowLink({ r, right }: { r: NewAccountRow; right: string }) {
  const to = r.facilityId ? `/account-management/facilities/${r.facilityId}` : '/new-accounts';
  return (
    <Link to={to} className="group flex items-center justify-between gap-3 px-2 py-2 hover:bg-tcs-surface">
      <span className="min-w-0">
        <span className="block truncate text-sm font-medium text-tcs-text">{r.displayName}</span>
        <span className="flex items-center gap-1 truncate text-[11px] text-tcs-text-secondary">
          {r.marketRegion && <MapPin className="h-3 w-3 shrink-0" />}
          {metaLine(r)}
        </span>
      </span>
      <span className="flex shrink-0 items-center gap-1 text-right text-[11px] font-medium text-tcs-text-secondary">
        {right}
        <ArrowUpRight size={12} className="text-tcs-text-faint group-hover:text-tcs-text-secondary" aria-hidden />
      </span>
    </Link>
  );
}
